import React, { useMemo } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  FileFormatIcon,
  Flex,
  ListItem,
  Text,
} from '@/components/ui';
import {
  IconCheck,
  IconChevronDown,
  IconCloudDataConnection,
  IconDatabase,
  IconEye,
  IconQuestionMark,
  IconTable,
  IconX,
} from '@tabler/icons-react';
import { ColumnMapping, ColumnMappingType, SelectedFile, StudyForm } from '../types';
import { METADATA_PATTERNS } from '../../constants/metadataPatterns';
import { FileInfoHint } from './FileInfoHint';

// New selector component
import { ColumnFieldSelector } from '../ColumnFieldSelector';

// Use shared metadata patterns
const metadataTypes = METADATA_PATTERNS;

interface FileInfoBlockProps {
  file: SelectedFile;
  showColumns?: boolean;
  matchingColumns?: string[];
  getColumnColor?: (
    columnName: string
  ) => 'success' | 'info' | 'default' | 'warning';
  getColumnVariant?: (columnName: string) => 'surface' | 'dashed' | 'soft';
  getColumnMapping?: (columnName: string) => ColumnMapping | undefined;
  onColumnClick?: (
    columnName: string,
    type?: ColumnMappingType,
    metadataType?: string
  ) => void;
  onColumnSelectForFormData?: (columnName: string) => void; // Opens modal for form data column selection
  className?: string;
  handlePreview?: () => void;
  isSubform?: boolean; // Whether the mapped form is a subform
  hoveredElement?: {
    type: 'column' | 'field' | 'metadata';
    name: string;
  } | null;
  onElementHover?: (
    element: { type: 'column' | 'field' | 'metadata'; name: string } | null
  ) => void;
  getElementOpacity?: (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => number;
  // New props for enhanced selectors
  useNewSelector?: boolean;
  form?: StudyForm; // StudyForm
  fileMappings?: ColumnMapping[];
  fileId?: string;
  onColumnFieldMappingChange?: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
}

export const FileInfoBlock: React.FC<FileInfoBlockProps> = ({
  file,
  showColumns = true,
  getColumnColor,
  getColumnVariant,
  getColumnMapping,
  onColumnClick,
  onColumnSelectForFormData,
  handlePreview,
  isSubform = false,
  onElementHover,
  getElementOpacity,
  // New props
  useNewSelector = false,
  form,
  fileMappings = [],
  fileId,
  onColumnFieldMappingChange,
}) => {
  const fileName =
    file.source === 'uploaded' ? file.file!.name : file.csvFile!.filename;

  // Separate columns into metadata and non-metadata groups
  const { metadataColumns, regularColumns } = useMemo(() => {
    const metadataCols: string[] = [];
    const regularCols: string[] = [];

    file.metadata.columns.forEach(col => {
      // Check if column is mapped as metadata
      let mapping: ColumnMapping | undefined;
      if (fileId && fileMappings.length > 0) {
        // Use fileMappings if available
        mapping = fileMappings.find(
          m => m.fileId === fileId && m.columnName === col
        );
      } else if (getColumnMapping) {
        // Fallback to getColumnMapping
        mapping = getColumnMapping(col);
      }

      if (mapping && mapping.mappingType === 'metadata') {
        metadataCols.push(col);
      } else {
        regularCols.push(col);
      }
    });

    return { metadataColumns: metadataCols, regularColumns: regularCols };
  }, [file.metadata.columns, fileId, fileMappings, getColumnMapping]);

  // Render a single column badge
  const renderColumn = (col: string, index: number) => {
    const color = getColumnColor ? getColumnColor(col) : 'default'; // Don't use matching columns for color - only actual mappings

    // Determine variant based on mapping status
    let variant: 'surface' | 'dashed' | 'soft' = 'dashed'; // default for unmapped
    if (getColumnVariant) {
      variant = getColumnVariant(col);
    } else {
      // Auto-determine variant based on actual mapping (not potential matches)
      let mapping: ColumnMapping | undefined;
      if (fileId && fileMappings.length > 0) {
        mapping = fileMappings.find(
          m => m.fileId === fileId && m.columnName === col
        );
      } else if (getColumnMapping) {
        mapping = getColumnMapping(col);
      }

      if (mapping) {
        if (mapping.mappingType === 'metadata')
          variant = 'surface'; // blue for metadata
        else if (mapping.mappingType === 'formField')
          variant = 'surface'; // green for form fields
        else if (mapping.mappingType === 'ignored') variant = 'soft'; // default for ignored
      }
      // All unmapped columns (regardless of potential matches) remain dashed
    }

    const showCheck =
      color === 'success' || color === 'info' || color === 'warning';

    const canReclassify = onColumnClick; // Allow reclassification if handler exists

    if (canReclassify) {
      // Use new ColumnFieldSelector dropdown when enabled
      if (
        useNewSelector &&
        form &&
        fileId &&
        onColumnFieldMappingChange
      ) {
        return (
          <div
            key={index}
            style={{
              transition: 'all 0.2s ease',
              opacity: getElementOpacity
                ? getElementOpacity('column', col)
                : 1,
            }}
            onMouseEnter={() =>
              onElementHover &&
              onElementHover({ type: 'column', name: col })
            }
            onMouseLeave={() =>
              onElementHover && onElementHover(null)
            }
          >
            <ColumnFieldSelector
              columnName={col}
              form={form}
              fileMappings={fileMappings}
              fileId={fileId}
              onMappingChange={onColumnFieldMappingChange}
              showAsDropdownContent={false}
              triggerLabel={col}
              triggerIcon={
                showCheck ? <IconCheck /> : <IconQuestionMark />
              }
              triggerVariant={variant}
              triggerColor={color}
            />
          </div>
        );
      }

      // Original dropdown implementation
      return (
        <DropdownMenu key={index}>
          <DropdownMenuTrigger>
            <Badge
              color={color}
              variant={variant}
              size="small"
              startIcon={
                showCheck ? <IconCheck /> : <IconQuestionMark />
              }
              endIcon={<IconChevronDown />}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: getElementOpacity
                  ? getElementOpacity('column', col)
                  : 1,
              }}
              onMouseEnter={() =>
                onElementHover &&
                onElementHover({ type: 'column', name: col })
              }
              onMouseLeave={() =>
                onElementHover && onElementHover(null)
              }
            >
              {col}
            </Badge>
          </DropdownMenuTrigger>
          <DropdownMenuContent variant="soft">
            <DropdownMenuSub asChild>
              <DropdownMenuSubTrigger asChild>
                <ListItem
                  startIcon={<IconDatabase />}
                  primary="Metadata"
                  secondary="System field (subjectid, siteid, etc.)"
                />
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {metadataTypes
                  .filter(
                    meta => !isSubform || meta.type !== 'mainFormId'
                  ) // Hide mainFormId for non-subforms
                  .map(metaType => {
                    let columnMapping: ColumnMapping | undefined;
                    if (fileId && fileMappings.length > 0) {
                      columnMapping = fileMappings.find(
                        m => m.fileId === fileId && m.columnName === col
                      );
                    } else if (getColumnMapping) {
                      columnMapping = getColumnMapping(col);
                    }
                    const isSelected =
                      columnMapping?.mappingType === 'metadata' &&
                      columnMapping?.metadataType?.type === metaType.type;

                    return (
                      <DropdownMenuItem key={metaType.type} asChild>
                        <ListItem
                          primary={metaType.label}
                          secondary={metaType.description}
                          startIcon={
                            isSelected ? (
                              <IconCheck size={14} />
                            ) : undefined
                          }
                          onClick={() =>
                            onColumnClick(
                              col,
                              'metadata',
                              metaType.type
                            )
                          }
                          style={{
                            backgroundColor: isSelected
                              ? 'var(--blue-3)'
                              : undefined,
                            fontWeight: isSelected
                              ? 'bold'
                              : undefined,
                          }}
                        />
                      </DropdownMenuItem>
                    );
                  })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconTable />}
                primary="Map to Form Field"
                secondary="Choose which form field to map this column to"
                onClick={() =>
                  onColumnSelectForFormData &&
                  onColumnSelectForFormData(col)
                }
              />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconX />}
                primary="Unclassified"
                secondary="Remove classification"
                onClick={() => onColumnClick(col, 'ignored')}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Badge
        key={index}
        color={color}
        variant="outlined"
        size="small"
        startIcon={showCheck ? <IconCheck /> : <IconQuestionMark />}
        style={{
          cursor: 'default',
          transition: 'all 0.2s ease',
          opacity: getElementOpacity
            ? getElementOpacity('column', col)
            : 1,
        }}
        onMouseEnter={() =>
          onElementHover &&
          onElementHover({ type: 'column', name: col })
        }
        onMouseLeave={() => onElementHover && onElementHover(null)}
      >
        {col}
      </Badge>
    );
  };

  return (
    <>
      <Flex direction="column" gap="2" flexGrow="1" className="mapping-block">
        <Button
          variant="surface"
          asChild
          onClick={handlePreview}
          className="dropdown"
        >
          <ListItem
            align="center"
            startIcon={
              <FileFormatIcon
                fileName={fileName}
                mimeType="text/csv"
                size="small"
              />
            }
            endIcon={
              <Button
                onClick={handlePreview}
                isIconOnly
                size="small"
                variant="ghost"
              >
                <IconEye />
              </Button>
            }
          >
            <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
              <Text
                size="2xsmall"
                weight="bold"
                color="textPrimary"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fileName}
              </Text>
              {file.source === 'csv-importer' && (
                <Badge
                  size="xsmall"
                  color="feature"
                  variant="surface"
                  startIcon={<IconCloudDataConnection />}
                  style={{ flexShrink: 0 }}
                >
                  CSV Upload
                </Badge>
              )}
            </Flex>
          </ListItem>
        </Button>
        <FileInfoHint file={file} />

        {/* Metadata Columns - separate block */}
        {showColumns && metadataColumns.length > 0 && (
          <Flex gap="1" wrap="wrap">
            {metadataColumns.map((col, index) => renderColumn(col, index))}
          </Flex>
        )}

        {/* Regular File Columns */}
        {showColumns && regularColumns.length > 0 && (
          <Flex gap="1" wrap="wrap">
            {regularColumns.map((col, index) => renderColumn(col, index))}
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default FileInfoBlock;
